// product-type-group-feature-list.component.ts
import { Component, Input, OnInit, SimpleChanges, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { AttributeInterface } from 'src/app/model/attributes/attribute-interface';
import { PtgAttributeValidationInterface } from 'src/app/model/attributes/ptg-attribute-validation-interface';
import { ProductTypeGroupFeatureDisplayInterface, ProductTypeGroupFeaturesDisplayInterface } from 'src/app/model/marketplace/product-type-group-features-interface';
import { ProductVariantInterface } from 'src/app/model/products/product-variant-interface';
import { VariantVersionInterface } from 'src/app/model/products/variant-version-interface';
import { PtgVersionInterface } from 'src/app/model/ptgs/ptg-version-interface';
interface FeatureRow {
  featureName: string;
  datatype: string;
  values: string[]; // one per variant/version column
}
@Component({
    selector: 'app-product-type-group-feature-list',
    templateUrl: './product-type-group-feature-list.component.html',
    styleUrls: [
        './product-type-group-feature-list.component.scss',
        '../product-type-group-feature-display.component.scss'
    ],
    standalone: false
})
export class ProductTypeGroupFeatureListComponent implements OnInit {
  @Input() title?: string;
  @Input() tooltipText?: string;
  @Input() comparisonMode?: string;
  @Input() features?: ProductTypeGroupFeatureDisplayInterface[];
  @Input() variantPTGFeaturesList?: any[]; // Array of variants
  expandedPTGs: { [ptgName: string]: boolean } = {};
  @Input() ptgFeatureComparisonData?: any;
  @Input() ptg_names?: string[] = [];
  ptgVersionFeatures: ProductTypeGroupFeaturesDisplayInterface[];
  version: VariantVersionInterface;
  featureRows: FeatureRow[] = [];
  columnHeaders: string[] = [];
  variantNameToIndices: { [variantName: string]: number[] } = {};
  certifiedColumns: boolean[] = [];

  @ViewChildren('headerSpan') headerSpans!: QueryList<ElementRef>;
  overflowingHeaders: boolean[] = [];
  private needsOverflowMeasurement = false;

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ptgFeatureComparisonData'] || changes['comparisonMode']) {
      this.rebuildTable();
    }
  }
  
  private rebuildTable() {
    if (!this.ptgFeatureComparisonData) return;
    this.needsOverflowMeasurement = true;
    this.overflowingHeaders = [];

    if (this.comparisonMode) { // this.comparisonMode is only defined for variant comparison
      this.buildFeatureTable2(); // for variant comparison
    } else {
      this.buildFeatureTable();  // for version comparison
    }
  }

  ngAfterViewChecked() {
    if (!this.needsOverflowMeasurement) return;
    this.measureOverflowForHeaders();
    this.needsOverflowMeasurement = false;
  }

  private updatePTGlist(version: VariantVersionInterface): any {
    this.ptgVersionFeatures = version.ptgVersions
      .sort((a, b) =>
        a.ptgName.toLowerCase().localeCompare(b.ptgName.toLowerCase())
      )
      .map((ptgVersion) => this.getPTGFeatures(ptgVersion));
    this.version = version;
    this.ptg_names = version.productTypeNames;
    return this.ptgVersionFeatures;
  }

  isBooleanFeature(feature: any): boolean {
    return feature?.datatype === 'BOOLEAN';
  }

  togglePTG(ptgName: string): void {
    this.expandedPTGs[ptgName] = !this.expandedPTGs[ptgName];
  }



  getAllPTGNames(): any {
    const ptgNames = new Set<string>();
    const versions: any[] = [];
    for (const variant of this.variantPTGFeaturesList) {
      for (const version of variant.variant.variantVersions) {
        this.version = version;
        versions.push(this.updatePTGlist(version));

      }
    }
    return versions;
  }




  private getPTGFeatures(
    ptgVersion: PtgVersionInterface
  ): ProductTypeGroupFeaturesDisplayInterface {
    const ptgVersionFeatures: ProductTypeGroupFeaturesDisplayInterface = {
      ptgName: ptgVersion.ptgName,
      ptgDescription: this.removeHTMLTags(ptgVersion.ptgDescription),
      mandatoryFeatures: [],
      optionalFeatures: [],
      doesProjectFulfillAllFeaturesOfPTGVersion: true
    };
    for (const attributeVal of ptgVersion.attributeValidations) {
      const feature: ProductTypeGroupFeatureDisplayInterface = {
        featureName: attributeVal.attribute.name,
        featureDescription: attributeVal.attribute.description,
        featureValidation: this.getValidationValue(attributeVal),
        datatype: attributeVal.attribute.datatype
      };
      if (attributeVal.optionalValidation) {
        ptgVersionFeatures.optionalFeatures.push(feature);
      } else {
        ptgVersionFeatures.mandatoryFeatures.push(feature);
        if (
          !this.isAttributeValueFulfillingValidation(
            attributeVal.attribute,
            feature.featureValidation + ''
          )
        ) {
          ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
            false;
        }
      }
    }
    if (ptgVersionFeatures.mandatoryFeatures.length === 0) {
      ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
        false;
    }
    ptgVersionFeatures.mandatoryFeatures.sort((a, b) =>
      a.featureName
        .toLowerCase()
        .localeCompare(b.featureName.toLowerCase())
    );
    ptgVersionFeatures.optionalFeatures.sort((a, b) =>
      a.featureName
        .toLowerCase()
        .localeCompare(b.featureName.toLowerCase())
    );
    return ptgVersionFeatures;
  }


  private removeHTMLTags(text: string): string {
    return text.replace(/<[^>]*>/g, '');
  }

  private getValidationValue(
    attribute: PtgAttributeValidationInterface
  ): string {
    const validation = this.version.attributeValidationValues.find(
      (validationValue) =>
        validationValue.attribute.id === attribute.attribute.id
    );
    const validationValue = validation == null ? '' : validation.value;
    if (
      attribute.attribute.datatype === 'LIST' &&
      validationValue != null
    ) {
      return validationValue.replace(/;/g, ', ');
    }
    return validationValue;
  }

  private isAttributeValueFulfillingValidation(
    attribute: AttributeInterface,
    value: string
  ) {
    const validation = attribute.validations[0];
    switch (attribute.datatype) {
      case 'NUMERIC':
        return this.isNumberInRange(
          Number(value),
          validation.minSize,
          validation.maxSize
        );
      case 'BOOLEAN':
        return (
          attribute.validations[0].value == null ||
          value == null ||
          attribute.validations[0].value + '' == value + ''
        );
      case 'LIST':
        return this.isListValid(
          validation.values,
          validation.minSize,
          validation.maxSize,
          value.split(', ')
        );
      case 'STRING':
        return this.isNumberInRange(
          value.length,
          validation.minSize,
          validation.maxSize
        );
    }
  }

  private isListValid(
    validationValues: string[],
    minCount: number,
    maxCount: number,
    selectedValues: string[]
  ): boolean {
    for (const selectedValue of selectedValues) {
      if (!validationValues.includes(selectedValue)) {
        return false;
      }
    }
    return this.isNumberInRange(selectedValues.length, minCount, maxCount);
  }

  private isNumberInRange(value: number, min: number, max: number): boolean {
    return (min == null || min <= value) && (max == null || max >= value);
  }

  buildFeatureTable() {
    this.featureRows = [];
    this.columnHeaders = [];

    const allFeatures: { [name: string]: { datatype: string, description: string, values: string[] } } = {};
    this.columnHeaders = this.ptgFeatureComparisonData.variants
      .map(v => v.versionName.trim())
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));

    this.certifiedColumns = this.columnHeaders.map(versionName => {
      const variant = this.ptgFeatureComparisonData.variants
          .find(v => v.versionName.trim() === versionName);
      return variant?.isCertified === true;
    });

    // Collect all unique features (mandatory + optional)
    this.ptgFeatureComparisonData.variants.forEach((variant, colIdx) => {
      const features = [
        ...(variant.mandatoryFeatures || []),
        ...(variant.optionalFeatures || [])
      ];
      features.forEach(f => {
        if (!allFeatures[f.featureName]) {
          allFeatures[f.featureName] = {
            datatype: f.datatype,
            description: f.featureDescription,
            values: Array(this.ptgFeatureComparisonData.variants.length).fill('')
          };
        }
        // Use null, empty string, or undefined as "not available"
        allFeatures[f.featureName].values[colIdx] = f.featureValidation ?? '';
      });
    });

    // Convert to array for template
    this.featureRows = Object.entries(allFeatures).map(([featureName, obj]) => ({
      featureName,
      datatype: obj.datatype,
      featureDescription: obj.description,
      values: obj.values
    }));
  }

  buildFeatureTable2() {
    // 0. Reset all structures
    this.variantNameToIndices = {};
    this.featureRows = [];
    this.columnHeaders = [];

    // 1. Map normalized variant names to all their column indices (handle duplicates)

    this.ptgFeatureComparisonData.variants.forEach((variant, idx) => {
      const rawName = variant.variantName ?? variant.versionName ?? '';
      const name = rawName.trim().toLowerCase();
      if (!this.variantNameToIndices[name]) this.variantNameToIndices[name] = [];
      this.variantNameToIndices[name].push(idx);
    });

    // 2. Get unique variant names for columns (display original name, but use normalized for logic)
    const columnHeadersRaw = Object.keys(this.variantNameToIndices);
    this.columnHeaders = columnHeadersRaw.map(name => {
      // Find first variant with this normalized name to get display name
      const idx = this.variantNameToIndices[name][0];
      const variant = this.ptgFeatureComparisonData.variants[idx];
      return (variant.variantName ?? variant.versionName ?? '').trim();
    });

    this.certifiedColumns = this.columnHeaders.map((_, colIdx) => 
      this.isVariantCertified(colIdx)
    );

    // 3. Collect all unique features (mandatory + optional)
    const allFeatures: { [name: string]: { description: string, datatype: string, values: string[] } } = {};
    this.ptgFeatureComparisonData.variants.forEach((variant, colIdx) => {
      const features = [
        ...(variant.mandatoryFeatures || []),
        ...(variant.optionalFeatures || [])
      ];
      features.forEach(f => {
        if (!allFeatures[f.featureName]) {
          allFeatures[f.featureName] = {
            datatype: f.datatype,
            description: f.featureDescription,
            values: Array(this.columnHeaders.length).fill('')
          };
        }
      });
    });

    // 4. For each feature, for each unique variant name, set value to '1' if any duplicate column has a truthy value
    Object.entries(allFeatures).forEach(([featureName, obj]) => {
      columnHeadersRaw.forEach((normalizedVariantName, colIdx) => {
        // Check all indices for this variant name
        const indices = this.variantNameToIndices[normalizedVariantName];
        let found = false;
        for (const idx of indices) {
          const variant = this.ptgFeatureComparisonData.variants[idx];
          const features = [
            ...(variant.mandatoryFeatures || []),
            ...(variant.optionalFeatures || [])
          ];
          const f = features.find(f => f.featureName === featureName);
          if (f && (f.featureValidation === true || f.featureValidation === 'true')) {
            found = true;
            break;
          }
        }
        obj.values[colIdx] = found ? 'true' : 'false';
      });
    });

    // 5. Convert to array for template
    this.featureRows = Object.entries(allFeatures).map(([featureName, obj]) => ({
      featureName,
      featureDescription: obj.description,
      datatype: obj.datatype,
      values: obj.values
    }));
  }

  getAllVariantFeatures(): string[] {
    const featureSet = new Set<string>();
    for (const variant of this.variantPTGFeaturesList || []) {
      for (const feature of [...(variant.mandatoryFeatures || []), ...(variant.optionalFeatures || [])]) {
        featureSet.add(feature.featureName);
      }
    }
    return Array.from(featureSet).sort();
  }

  getVariantFeatureValue(variant: any, featureName: string): string {
    for (const feature of [...(variant.mandatoryFeatures || []), ...(variant.optionalFeatures || [])]) {
      if (feature.featureName === featureName) {
        if (feature.datatype === 'BOOLEAN') {
          return feature.featureValidation === true || feature.featureValidation === 'true' ? 'true' : 'false';
        }
        return feature.featureValidation ?? '';
      }
    }
    return '–';
  }

  doesProjectFulfillAllFeaturesOfPTGVersion(index: number): boolean {
    const variant = this.ptgFeatureComparisonData.variants[index];
    if (!variant) return false;
    const mandatoryOk = variant.fulfillsAllMandatory;
    return mandatoryOk;
  }

  doesVariantFulfillAllFeaturesOfPTGVersion(colIdx: number): boolean {
    if (this.comparisonMode) {
      const columnHeadersRaw = Object.keys(this.variantNameToIndices);
      const normalizedName = columnHeadersRaw[colIdx];
      if (!normalizedName) return false;
      const indices = this.variantNameToIndices[normalizedName] || [];
      if (indices.length === 0) return false;
      for (const idx of indices) {
        const variant = this.ptgFeatureComparisonData.variants[idx];
        if (variant?.fulfillsAllMandatory === true) {
          return true;
        }
        if (variant?.fulfillsAllMandatory === 'true') {
          return true;
        }
      }
      return false;
    }
    else if (!this.comparisonMode) {
      const variant = this.ptgFeatureComparisonData.variants[colIdx];
      if (!variant) return false;
      const mandatoryOk = variant.fulfillsAllMandatory;
      return mandatoryOk;
    }

  }

  trackByValue(index: number, value: string): string {
    return index + '-' + value;
  }

  trackByFeature(index: number, feature: any): string {
    return feature.featureName + '-' + index;
  }

  private isVariantCertified(colIdx: number): boolean {
    if (!this.ptgFeatureComparisonData?.variants) return false;

    if (this.comparisonMode === 'variant') {
        const columnHeadersRaw = Object.keys(this.variantNameToIndices);
        const normalizedName = columnHeadersRaw[colIdx];
        if (!normalizedName) return false;
        const indices = this.variantNameToIndices[normalizedName] || [];
        if (indices.length === 0) return false;

        return this.ptgFeatureComparisonData.variants[indices[0]]?.isCertified === true;
    } else {
        // For Version comparison mode, we can directly check the column index
        return this.ptgFeatureComparisonData.variants[colIdx]?.isCertified === true;
    }
  }

  /**
   * Measures if header name overflows its cell and sets the overflowingHeaders array accordingly to true or false.
   * This is used to conditionally show tooltips only for headers that are actually shortened..
   */
  private measureOverflowForHeaders(): void {
    this.overflowingHeaders = this.headerSpans.toArray().map(el => {
      const span = el.nativeElement as HTMLElement;
      return span.scrollWidth > span.clientWidth;
    });
  }

}
